import { resolveTootPrimitive } from "../../../../client";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import type { INotFoundBlockProps } from "./NotFoundBlock.props";

const TITLE_ID = "toot-not-found-title";

export const NotFoundBlock = ({
  description,
  homeHref,
  homeLabel,
  parentClass,
  statusCode,
  title,
}: INotFoundBlockProps) => {
  const Link = resolveTootPrimitive("Link");
  if (!statusCode && !title && !description && !(homeHref && homeLabel)) {
    return null;
  }

  return (
    <TootSectionShell className="relative" rootClassName={parentClass} spacing="loose" width="wide">
      <article
        aria-labelledby={title ? TITLE_ID : undefined}
        className="toot-heat-panel relative isolate overflow-hidden p-[clamp(1.25rem,4vw,3.5rem)] text-toot-foreground"
        data-toot-block="notFound"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 toot-grid-bg opacity-45"
        />

        <div className="relative z-10 grid min-w-0 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,0.78fr)] lg:gap-14">
          <div className="flex min-w-0 max-w-3xl flex-col items-start">
            <div className="mb-5 inline-flex min-h-8 items-center gap-3 rounded-[var(--radius-toot-panel)] border border-toot-orange/40 bg-toot-orange/10 px-3 text-xs font-black uppercase tracking-[0.16em] text-toot-orange">
              <span
                aria-hidden="true"
                className="size-2 bg-toot-red shadow-[0_0_1rem_color-mix(in_oklab,var(--color-toot-red)_45%,transparent)]"
              />
              Route unresolved
            </div>

            {statusCode ? (
              <p className="m-0 text-[4.5rem] font-black leading-none tracking-[-0.06em] text-toot-red sm:text-8xl lg:text-[9rem]">
                {statusCode}
              </p>
            ) : null}
            {title ? (
              <h1
                className="mt-4 max-w-[14ch] text-balance text-[2.35rem] font-black leading-[0.98] tracking-[-0.045em] text-toot-foreground [overflow-wrap:anywhere] md:text-[4.5rem]"
                id={TITLE_ID}
              >
                {title}
              </h1>
            ) : null}
            {description ? (
              <p className="mt-5 max-w-[58ch] text-pretty text-base leading-7 text-toot-muted sm:text-lg sm:leading-8">
                {description}
              </p>
            ) : null}

            {homeHref && homeLabel ? (
              <Link
                aria-label={homeLabel}
                className="mt-8 inline-flex min-h-12 items-center justify-center gap-3 rounded-full bg-toot-red px-6 text-sm font-black text-white transition-[background-color,transform] duration-200 ease-out hover:-translate-y-0.5 hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                href={homeHref}
              >
                <span>{homeLabel}</span>
                <span aria-hidden="true">→</span>
              </Link>
            ) : null}
          </div>

          <div
            aria-hidden="true"
            className="relative overflow-hidden rounded-[var(--radius-toot-frame)] border border-toot-grid-strong bg-toot-surface p-4 shadow-[0_1.5rem_4rem_rgba(23,25,29,0.16)] sm:p-5"
          >
            <div className="rounded-[var(--radius-toot-panel)] border border-toot-grid-strong bg-toot-surface-raised p-5 sm:p-6">
              <div className="flex items-center justify-between gap-4 border-b border-toot-grid-strong pb-4 text-xs font-black uppercase tracking-[0.14em]">
                <span>Route resolver</span>
                <span className="flex items-center gap-2 text-toot-orange">
                  <span className="size-2 rounded-full bg-toot-red" />
                  Offline
                </span>
              </div>

              <div className="relative grid min-h-64 grid-cols-[4.25rem_minmax(4rem,1fr)_4.25rem] items-center py-10">
                <div className="relative z-10 grid size-[4.25rem] place-items-center rounded-[var(--radius-toot-frame)] border border-toot-grid-strong bg-white text-xs font-black uppercase tracking-[0.1em] text-toot-foreground shadow-sm">
                  Home
                </div>
                <div className="relative h-px border-t-2 border-dashed border-toot-grid-strong">
                  <span className="absolute left-1/2 top-1/2 grid size-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-4 border-toot-surface-raised bg-toot-red text-sm font-black text-white shadow-[0_0.75rem_2rem_color-mix(in_oklab,var(--color-toot-red)_30%,transparent)]">
                    {statusCode}
                  </span>
                </div>
                <div className="relative z-10 grid size-[4.25rem] place-items-center rounded-[var(--radius-toot-frame)] border border-toot-grid-strong bg-white text-center text-[0.65rem] font-black uppercase leading-4 tracking-[0.08em] text-toot-muted shadow-sm">
                  Missing
                  <br />
                  route
                </div>
              </div>

              <p className="m-0 border-t border-toot-grid-strong pt-4 text-sm leading-6 text-toot-muted">
                The requested path returned no matching page record.
              </p>
            </div>
          </div>
        </div>
      </article>
    </TootSectionShell>
  );
};

NotFoundBlock.displayName = "Block::NotFoundBlock";
